import { useEffect } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { getIdTokenResult, onAuthStateChanged, signOut } from "firebase/auth";
import { auth, db } from "../services/firebase";
import { Globals } from "../globals";

/**
 * Keeps the signed-in user's claims current (GOD-41). Functions write
 * accountSignals/{uid} whenever an admin changes the account's Gemeinde or
 * role; a signal newer than the current ID token triggers a fresh token, which
 * useAuth picks up. If the change took rights away, the refresh tokens were
 * revoked: the refresh fails and the session ends.
 *
 * Mount once, inside the authenticated layout.
 */
export function useClaimsRefresh(onSignedOut?: () => void): void {
    useEffect(() => {
        let stopSignal: (() => void) | null = null;
        const stopAuth = onAuthStateChanged(auth, (user) => {
            stopSignal?.();
            stopSignal = null;
            if (!user) return;
            stopSignal = onSnapshot(
                doc(db, Globals.ACCOUNT_SIGNALS_COLLECTION, user.uid),
                async (snap) => {
                    const changedAt = snap.get("claimsChangedAt")?.toMillis?.() as number | undefined;
                    if (!changedAt) return;
                    const issuedAt = Date.parse((await getIdTokenResult(user)).issuedAtTime);
                    if (changedAt <= issuedAt) return;
                    try {
                        await user.getIdToken(true);
                    } catch {
                        // Revoked: access was taken away.
                        await signOut(auth);
                        onSignedOut?.();
                    }
                },
                () => undefined
            );
        });
        return () => {
            stopSignal?.();
            stopAuth();
        };
    }, [onSignedOut]);
}
